import React, { useRef, useEffect, useState } from "react";
import emailjs from "emailjs-com";
import { FaGithub } from "react-icons/fa";
import "./App.css";

const getDimensions = (ele) => {
  if (!ele) return { height: 0, offsetTop: 0, offsetBottom: 0 };
  const { height } = ele.getBoundingClientRect();
  const offsetTop = ele.offsetTop;
  const offsetBottom = offsetTop + height;
  return { height, offsetTop, offsetBottom };
};

const scrollTo = (ele) => {
  ele.scrollIntoView({ behavior: "smooth", block: "start" });
};

function App() {
  const [visibleSection, setVisibleSection] = useState();
  const headerRef = useRef(null);
  const aboutRef = useRef(null);
  const projectsRef = useRef(null);
  const contactRef = useRef(null);
  const formRef = useRef(null);

  const sectionRefs = [
    { section: "About", ref: aboutRef },
    { section: "Projects", ref: projectsRef },
    { section: "Contact", ref: contactRef },
  ];

  useEffect(() => {
    const publicKey = "wAEaNeij2bT-Vai2a";
    if (publicKey) {
      emailjs.init(publicKey);
    } else {
      console.warn("REACT_APP_EMAILJS_PUBLIC_KEY is not set.");
    }
  }, []);

  const sendEmail = async (e) => {
    e.preventDefault();

    const serviceId  = "service_nz8bqiw";
    const templateId = "template_a6ivp9e";
    const publicKey  = "wAEaNeij2bT-Vai2a";

    if (!serviceId || !templateId || !publicKey) {
      alert("Email service is not configured (missing IDs or public key).");
      return;
    }

    try {
      const res = await emailjs.sendForm(
        serviceId,
        templateId,
        formRef.current,
        publicKey
      );
      console.log("EmailJS response:", res);
      e.target.reset();
    } catch (err) {
      console.error("EmailJS error:", err);
      if (String(err?.text || err?.message || "").includes("Origin")) {
        alert("Blocked by domain (origin) settings. Add http://localhost:3000 in EmailJS Allowed Origins.");
      } else if (String(err?.text || err?.message || "").includes("template")) {
        alert("Template mismatch: check field names in EmailJS template match from_name, reply_to, message.");
      } else {
        alert("Could not send. Check console for details.");
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const { height: headerHeight } = getDimensions(headerRef.current);
      const scrollPosition = window.scrollY + headerHeight;
      const pageBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight;

      const selected = sectionRefs.find(({ ref }) => {
        const ele = ref.current;
        if (ele) {
          const { offsetBottom, offsetTop } = getDimensions(ele);
          return scrollPosition > offsetTop && scrollPosition < offsetBottom;
        }
        return false;
      });

      if (pageBottom) {
        setVisibleSection("Contact");
      } else if (selected && selected.section !== visibleSection) {
        setVisibleSection(selected.section);
      } else if (!selected && visibleSection) {
        setVisibleSection(undefined);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [visibleSection]);

  return (
    <div className="App">
      <div className="top-spacer" />

      <div className="content">
        <div className="sticky">
          <div className="header" ref={headerRef}>
            <button
              type="button"
              className={`header_link ${
                visibleSection === "About" ? "selected" : ""
              }`}
              onClick={() => scrollTo(aboutRef.current)}
            >
              About Me
            </button>
            <button
              type="button"
              className={`header_link ${
                visibleSection === "Projects" ? "selected" : ""
              }`}
              onClick={() => scrollTo(projectsRef.current)}
            >
              Projects
            </button>
            <button
              type="button"
              className={`header_link ${
                visibleSection === "Contact" ? "selected" : ""
              }`}
              onClick={() => scrollTo(contactRef.current)}
            >
              Contact
            </button>
          </div>
        </div>

        <div className="section" id="About" ref={aboutRef}>
          <img src="profile_pic.jpeg" alt="Profile" className="profile-image" />
          <h2>Hi, I'm Ranny!</h2>
          <p>
            I am full-stack developer located in Massachusetts. Completed my
            Master's degree in Data Science at the University of Denver Colorado
            in June 2025. I enjoy leveraging techology to creating and analyzing
            things that interest me. Check out what I am up to!
          </p>
          <a
            href="Ranny_Chin_Resume_Jan_2025.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="resume-button"
          >
            Resume
            <span className="arrow-icon">↗</span>
          </a>
        </div>

        <div className="solid-border" />

        <div className="section" id="Projects" ref={projectsRef}>
          <h2>Projects</h2>

          <div className="projects-container">
            {[
              {
                title: "Bluebikes Data Analysis",
                description:
                  "Analyzed publically available Boston Bluebikes data to determine best performing bike stations.",
                image: "bluebikes_map.png",
                liveDemo:
                  "https://docs.google.com/presentation/d/1-UBbd80vhxtHVCuyE5rRBZa7OhMmAvX9/edit?usp=sharing&ouid=115491308954830091515&rtpof=true&sd=true",
                sourceCode: "https://github.com/rannychin/4447_final_project",
                techStack: ["Python", "SQL", "R Studio"],
              },
              {
                title: "Olympic Data Analysis",
                description:
                  "Analyzed the Olympic data from 1901 - 2022 to determine factors that lead to success in the Olympic Games.",
                image: "olympic_histogram.png",
                liveDemo:
                  "https://docs.google.com/presentation/d/11JLKEydtXMOVlGc0LgrXtyjZ1ThAlTRf/edit?usp=sharing&ouid=115491308954830091515&rtpof=true&sd=true",
                sourceCode: "https://github.com/rannychin/olympic_analysis",
                techStack: ["Python", "R Studio"],
              },
              {
                title: "Miscellaneous 3D Modeling",
                description:
                  "Various 3D models designed in Fusion 360. Designs include a deck box, a custom video doorbell camera mount, a modular TCG deck box, and battery holder mount.",
                image: "project_collage.png",
                buttons: [
                  {
                    label: "View Models",
                    href: "https://www.printables.com/@RannyChin_3389063/models",
                    className: "project-button live-demo",
                  },
                ],
                techStack: ["Fusion 360"],
              },
            ].map((project, index) => {
              const defaultButtons = (p) =>
                [
                  p.liveDemo && {
                    label: p.liveDemoLabel ?? "Presentation",
                    href: p.liveDemo,
                    className: "project-button live-demo",
                  },
                  p.sourceCode && {
                    label: p.sourceCodeLabel ?? "Source Code",
                    href: p.sourceCode,
                    className: "project-button source-code",
                  },
                ].filter(Boolean);

              const buttons = project.buttons ?? defaultButtons(project);

              return (
                <div className="project-card" key={index}>
                  <img
                    src={project.image}
                    alt={project.title}
                    className="project-image"
                  />
                  <div className="project-info">
                    <h3>{project.title}</h3>
                    <p>{project.description}</p>

                    <div className="tech-stack">
                      {project.techStack.map((tech, idx) => (
                        <span key={idx} className="tech-keyword">
                          {tech}
                        </span>
                      ))}
                    </div>

                    <div className="project-buttons">
                      {buttons.map((btn, i) => (
                        <a
                          key={i}
                          href={btn.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={btn.className || "project-button"}
                        >
                          {btn.label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="section" id="Contact" ref={contactRef}>
          <h2>Contact Me</h2>
          <form ref={formRef} onSubmit={sendEmail}>
            <input type="hidden" name="email_subject" value="New message from Ranny’s site" />

            <input type="text" name="from_name" placeholder="Name" required />
            <input type="email" name="reply_to" placeholder="Email" required />
            <textarea name="message" placeholder="Message" required />

            <button type="submit">Submit</button>
          </form>
        </div>
      </div>

      <div className="bottom-spacer" />

      <footer className="footer">
        <a
          href="https://github.com/rannychin"
          target="_blank"
          rel="noopener noreferrer"
          className="github-link"
        >
          <FaGithub size={30} />
        </a>
        <p className="footer-text">Made by Ranny Chin © 2025</p>
      </footer>
    </div>
  );
}

export default App;
